import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { ErrorBoundary } from "@sentry/react";
import { SnackbarProvider } from "notistack";
import { type ReactNode, Suspense } from "react";

import { GreweTheme } from "~assets/grewe-theme";
import { GateQueriesPreloader } from "~components/App/GateQueriesPreloader/GateQueriesPreloader.tsx";
import { LoginPage } from "~components/App/LoginPage/LoginPage";
import { Routes } from "~components/App/Routes/Routes.tsx";
import { UpdatePill } from "~components/App/UpdatePill/UpdatePill.tsx";
import { ConfirmDialogProvider } from "~components/shared/ConfirmDialog/ConfirmDialogProvider";
import { GenericError } from "~components/shared/ErrorPages/GenericError.tsx";
import { ForeignTenantToolWarningProvider } from "~components/shared/ForeignTenantToolWarning/ForeignTenantToolWarningContext.tsx";
import { Spinner } from "~components/shared/Loading/Spinner/Spinner";
import { BusinessConfigProvider } from "~contexts/BusinessConfigContext/BusinessConfigContext";
import { CurrentEmployeeProvider } from "~contexts/CurrentEmployeeContext/CurrentEmployeeContext";
import { CurrentTenantProvider } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import { useI18n } from "~contexts/I18nContext/I18nContext.tsx";
import { ResourceProvider } from "~contexts/ResourceContext/ResourceContext.tsx";
import { SelectedManagerProvider } from "~contexts/SelectedManagerContext/SelectedManagerContext.tsx";
import { UserProfileProvider } from "~contexts/UserProfileContext/UserProfileContext.tsx";
import { InitializeReactQuery } from "~initializer/react-query";
import { lazyWithRetry } from "~lib/lazyWithRetry.ts";

// Pulls in dexie; nothing on the first paint needs it
const FailedMobileUploadsSyncRunner = lazyWithRetry(() =>
  import(
    "~components/Resources/PhotoDocumentation/PhotoUpload/FailedMobileUploadsSyncRunner.tsx"
  ).then((m) => ({ default: m.FailedMobileUploadsSyncRunner })),
);

/*
 * see https://mui.com/guides/interoperability/#css-injection-order
 */
export function GlobalCssPriority({ children }: { children: ReactNode }) {
  return <StyledEngineProvider injectFirst>{children}</StyledEngineProvider>;
}

export function App() {
  const { locale, datePickersTranslations } = useI18n();
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={datePickersTranslations}
    >
      <ThemeProvider theme={GreweTheme}>
        <SnackbarProvider
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <InitializeReactQuery>
            <BusinessConfigProvider>
              <GlobalCssPriority>
                <ErrorBoundary showDialog={true} fallback={GenericError}>
                  <UpdatePill />
                  <AuthenticatedTemplate>
                    <CurrentTenantProvider>
                      <GateQueriesPreloader
                        pathname={window.location.pathname}
                      />
                      <UserProfileProvider>
                        <CurrentEmployeeProvider>
                          <Suspense fallback={null}>
                            <FailedMobileUploadsSyncRunner />
                          </Suspense>
                          <ResourceProvider>
                            <SelectedManagerProvider>
                              <ConfirmDialogProvider>
                                <ForeignTenantToolWarningProvider>
                                  <Suspense fallback={<Spinner />}>
                                    <Routes />
                                  </Suspense>
                                </ForeignTenantToolWarningProvider>
                              </ConfirmDialogProvider>
                            </SelectedManagerProvider>
                          </ResourceProvider>
                        </CurrentEmployeeProvider>
                      </UserProfileProvider>
                    </CurrentTenantProvider>
                  </AuthenticatedTemplate>
                  <UnauthenticatedTemplate>
                    <LoginPage />
                  </UnauthenticatedTemplate>
                </ErrorBoundary>
              </GlobalCssPriority>
            </BusinessConfigProvider>
          </InitializeReactQuery>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}
